import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Breadcrumbs,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Divider,
  Checkbox,
  Alert,
  Stack,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { getBillingSystemSettings } from "app/services/api/billing";
import { saveDsokSystemSettings } from "app/services/api/dsokSystemSettingsApi";

const VAT_RATES = [23, 19, 5, 0];
const ALERT_EVENTS = [
  { key: "run_summary", label: "Súhrn behu" },
  { key: "generate_error", label: "Chyba generovania" },
  { key: "send_error", label: "Chyba odoslania" },
  { key: "abra_down", label: "Abra nedostupná" },
];

export default function BillingSystemSettingsPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [s, setS] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getBillingSystemSettings();
      const data = res?.data || null;
      setS(data);
      setRecipientsText((data?.alerts?.recipients || []).join("\n"));
    } catch (e) {
      enqueueSnackbar(
        t("pages.dsokBilling.system.loadError", {
          defaultValue: "Načítanie nastavení zlyhalo",
        }),
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  }, [t, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  // --- update helpers (immutable) ---
  const setDefaults = (k, v) =>
    setS((p) => ({ ...p, defaults: { ...p.defaults, [k]: v } }));
  const setAbra = (k, v) =>
    setS((p) => ({ ...p, abra: { ...p.abra, [k]: v } }));
  const setGeneration = (k, v) =>
    setS((p) => ({ ...p, generation: { ...(p.generation || {}), [k]: v } }));
  // Upomienky: jedna šablóna na email, jedna na SMS; per-kanál enabled + days.
  const setRemChannel = (channel, k, v) =>
    setS((p) => ({
      ...p,
      reminders: {
        ...p.reminders,
        [channel]: { ...((p.reminders && p.reminders[channel]) || {}), [k]: v },
      },
    }));
  const setSending = (k, v) =>
    setS((p) => ({ ...p, sending: { ...p.sending, [k]: v } }));
  const setAlerts = (k, v) =>
    setS((p) => ({ ...p, alerts: { ...p.alerts, [k]: v } }));

  const handleSave = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const recipients = recipientsText
        .split(/[\n,;]+/)
        .map((x) => x.trim())
        .filter(Boolean);
      const payload = { ...s, alerts: { ...s.alerts, recipients } };
      await saveDsokSystemSettings({ billing: payload });
      enqueueSnackbar(
        t("pages.dsokBilling.system.saved", { defaultValue: "Uložené" }),
        { variant: "success" },
      );
      load();
    } catch (e) {
      enqueueSnackbar(
        t("pages.dsokBilling.system.saveError", {
          defaultValue: "Uloženie zlyhalo",
        }),
        { variant: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading || !s) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", minHeight: 200, alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const d = s.defaults || {};
  const r = s.reminders || {};
  const re = r.email || {};
  const rs = r.sms || {};
  const daysToStr = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
  const snd = s.sending || {};
  const abra = s.abra || {};
  const gen = s.generation || {};

  return (
    <Box sx={{ px: 0, pb: 6 }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
          {t("sidebar.menuItem.home", { defaultValue: "Domov" })}
        </Link>
        <Link to="/dsok/system-settings" style={{ textDecoration: "none", color: "inherit" }}>
          {t("pages.systemSettings.breadcrumb", { defaultValue: "Nastavenia" })}
        </Link>
        <Typography color="text.primary">
          {t("pages.dsokBilling.system.title", { defaultValue: "Faktúrácia" })}
        </Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1">
          {t("pages.dsokBilling.system.title", { defaultValue: "Faktúrácia – systémové nastavenia" })}
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {t("pages.dsokBilling.system.save", { defaultValue: "Uložiť" })}
        </Button>
      </Stack>

      {/* 1. Abra + nesecretné defaulty */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Abra Flexi</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("pages.dsokBilling.system.abraNote", {
            defaultValue:
              "Prihlasovacie údaje Abry (používateľ/heslo, URL, firma) sú v .env súboroch a tu sa nenastavujú.",
          })}
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Typ dokladu (typDokl)"
              value={abra.defaultDocType || ""} onChange={(e) => setAbra("defaultDocType", e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label="MJ jednotka"
              value={abra.defaultMj || ""} onChange={(e) => setAbra("defaultMj", e.target.value)} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth size="small" label="Mena"
              value={abra.currency || ""} onChange={(e) => setAbra("currency", e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {/* 2. Predvolené hodnoty */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("pages.dsokBilling.system.defaults", { defaultValue: "Predvolené hodnoty (pre nových zákazníkov)" })}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3} md={2}>
            <TextField fullWidth size="small" type="number" label="Splatnosť (dni)"
              value={d.dueDays ?? 14} onChange={(e) => setDefaults("dueDays", Number(e.target.value))} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField select fullWidth size="small" label="Typ faktúrácie"
              value={d.billingType || "forward"} onChange={(e) => setDefaults("billingType", e.target.value)}>
              <MenuItem value="forward">Dopredu</MenuItem>
              <MenuItem value="backward">Dozadu</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField select fullWidth size="small" label="Frekvencia"
              value={d.frequency || "monthly"} onChange={(e) => setDefaults("frequency", e.target.value)}>
              <MenuItem value="monthly">Mesačná</MenuItem>
              <MenuItem value="annual">Ročná</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField select fullWidth size="small" label="DPH (%)"
              value={d.vatRate ?? 23} onChange={(e) => setDefaults("vatRate", Number(e.target.value))}>
              {VAT_RATES.map((v) => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={2} label="Predvolený úvodný text faktúry"
              value={d.introText || ""} onChange={(e) => setDefaults("introText", e.target.value)}
              placeholder="napr. Fakturujeme Vám za mesiac {{MM}}/{{RRRR}}" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={2} label="Záverečný text faktúry (platí pre všetkých)"
              value={d.closingText || ""} onChange={(e) => setDefaults("closingText", e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {/* 3. Automatické generovanie (cron) — konfigurovateľné */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">
            {t("pages.dsokBilling.system.cron", { defaultValue: "Automatické generovanie (cron)" })}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={gen.enabled === true}
                onChange={(e) => setGeneration("enabled", e.target.checked)}
              />
            }
            label={gen.enabled ? "Zapnuté" : "Vypnuté"}
          />
        </Stack>
        <Alert severity={gen.enabled ? "success" : "info"} sx={{ mb: 2 }}>
          {gen.enabled
            ? `Faktúry sa vytvoria automaticky každý mesiac ${gen.dayOfMonth || 1}. deň o ${gen.time || "00:00"} (Europe/Bratislava).`
            : "Automatické vytváranie faktúr je vypnuté."}
        </Alert>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Deň v mesiaci (1–28)"
              InputProps={{ inputProps: { min: 1, max: 28 } }}
              value={gen.dayOfMonth ?? 1}
              onChange={(e) => {
                const n = Math.min(28, Math.max(1, Math.trunc(Number(e.target.value) || 1)));
                setGeneration("dayOfMonth", n);
              }}
              disabled={!gen.enabled}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="time"
              label="Čas"
              InputLabelProps={{ shrink: true }}
              value={gen.time || "00:00"}
              onChange={(e) => setGeneration("time", e.target.value)}
              disabled={!gen.enabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Časové pásmo: Europe/Bratislava. Zmena sa prejaví po uložení
              (server sa nereštartuje).
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={snd.sendInvoices === true}
                  onChange={(e) => setSending("sendInvoices", e.target.checked)}
                />
              }
              label={t("pages.dsokBilling.system.sendInvoices", {
                defaultValue: "Odosielať faktúry zákazníkom",
              })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Kým je vypnuté, faktúry sa zákazníkovi neodošlú (ani ručne). Tvorba
              faktúr tým nie je dotknutá.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* 4. Upomienky */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t("pages.dsokBilling.system.reminders", { defaultValue: "Upomienky" })}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Upomienky sa posielajú <strong>len pre faktúry v stave „overdue"</strong>{" "}
          (po splatnosti a neuhradené). Tu sa nastavuje len <strong>či</strong> a{" "}
          <strong>kedy</strong> (dni po splatnosti) sa upomienky posielajú — samostatne
          pre email a SMS. <strong>Texty upomienok</strong> (email aj SMS) sa
          definujú na <strong>notifikačnom serveri</strong>{" "}
          (<code>notificationTemplates.json</code> → <code>invoice.reminder</code>),
          nie tu.
        </Alert>

        {/* Email */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Email</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={re.enabled === true}
                  onChange={(e) => setRemChannel("email", "enabled", e.target.checked)}
                />
              }
              label="Posielať email"
            />
          </Stack>
          <TextField fullWidth size="small" label="Dni po splatnosti (napr. 3, 7, 14)"
            helperText="Dni po splatnosti, kedy sa má poslať email"
            value={daysToStr(re.days)}
            onChange={(e) => setRemChannel("email", "days", e.target.value)} />
        </Paper>

        {/* SMS */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">SMS</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={rs.enabled === true}
                  onChange={(e) => setRemChannel("sms", "enabled", e.target.checked)}
                />
              }
              label="Posielať SMS"
            />
          </Stack>
          <TextField fullWidth size="small" label="Dni po splatnosti (napr. 7, 14)"
            helperText="Dni po splatnosti, kedy sa má poslať SMS"
            value={daysToStr(rs.days)}
            onChange={(e) => setRemChannel("sms", "days", e.target.value)} />
        </Paper>

        <Divider sx={{ mb: 1 }} />
        <FormControlLabel
          control={
            <Switch
              checked={snd.sendReminders === true}
              onChange={(e) => setSending("sendReminders", e.target.checked)}
            />
          }
          label={t("pages.dsokBilling.system.sendReminders", {
            defaultValue: "Odosielať upomienky zákazníkom",
          })}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Kým je vypnuté, upomienky sa zákazníkovi neodošlú (ani ručne). Pozn.:
          reálne odosielanie zákazníkom je aktuálne v testovacej fáze vypnuté.
        </Typography>
      </Paper>

      {/* 5. Odosielateľ */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("pages.dsokBilling.system.sender", { defaultValue: "Odosielateľ (faktúry / upomienky)" })}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Meno odosielateľa"
              value={snd.fromName || ""} onChange={(e) => setSending("fromName", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Email odosielateľa (from)"
              value={snd.fromEmail || ""} onChange={(e) => setSending("fromEmail", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Reply-to"
              value={snd.replyTo || ""} onChange={(e) => setSending("replyTo", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="BCC"
              value={snd.bcc || ""} onChange={(e) => setSending("bcc", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="SMS sender ID"
              value={snd.smsSenderId || ""} onChange={(e) => setSending("smsSenderId", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={<Switch checked={snd.attachPdf === true} onChange={(e) => setSending("attachPdf", e.target.checked)} />}
              label="Priložiť PDF faktúry"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 6. Interné alerty */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("pages.dsokBilling.system.alerts", { defaultValue: "Interné alerty" })}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" multiline minRows={3} label="Emaily adminov (každý na nový riadok)"
              value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">Posielať pri udalostiach</Typography>
            <Box>
              {ALERT_EVENTS.map((ev) => {
                const checked = (s.alerts?.events || []).includes(ev.key);
                return (
                  <FormControlLabel
                    key={ev.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => {
                          const cur = s.alerts?.events || [];
                          const next = checked ? cur.filter((x) => x !== ev.key) : [...cur, ev.key];
                          setAlerts("events", next);
                        }}
                      />
                    }
                    label={ev.label}
                  />
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {t("pages.dsokBilling.system.save", { defaultValue: "Uložiť" })}
        </Button>
      </Box>
    </Box>
  );
}
